import React, { useEffect } from 'react';
import { Heart, Loader2 } from 'lucide-react';
import useFollowAnalyst from '../../hooks/useFollowAnalyst';

/**
 * Heart icon follow button for analyst cards.
 *
 * Props:
 *   slug            — analyst slug
 *   initialFollowing — is_following from API
 *   onAuthRequired  — called if user is not logged in (show login popup)
 *   size            — icon size (default 16)
 */
export default function FollowButton({ slug, initialFollowing = false, onAuthRequired, size = 16 }) {
  const { following, toggle, loading, error, clearError } = useFollowAnalyst(slug, initialFollowing);

  // Auto-clear error after 3 seconds
  useEffect(() => {
    if (!error) return;
    const t = setTimeout(clearError, 3000);
    return () => clearTimeout(t);
  }, [error, clearError]);

  function handleClick(e) {
    e.preventDefault();
    e.stopPropagation();

    const token = localStorage.getItem('token');
    if (!token) {
      onAuthRequired?.();
      return;
    }
    toggle();
  }

  return (
    <div className="relative">
      <button
        onClick={handleClick}
        disabled={loading}
        title={following ? 'Unfollow' : 'Follow'}
        className={`flex items-center justify-center w-8 h-8 rounded-full transition-all duration-200 ${
          following
            ? 'bg-red-50 hover:bg-red-100'
            : 'bg-gray-100 hover:bg-gray-200'
        } disabled:opacity-50 disabled:cursor-not-allowed`}
      >
        {loading ? (
          <Loader2 size={size} className="animate-spin text-gray-400" />
        ) : (
          <Heart
            size={size}
            className={following ? 'text-red-500 fill-red-500' : 'text-gray-400'}
          />
        )}
      </button>

      {/* Error tooltip */}
      {error && (
        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-48 bg-gray-900 text-white text-xs rounded-lg px-3 py-2 text-center shadow-lg z-50 pointer-events-none">
          {error}
          <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900" />
        </div>
      )}
    </div>
  );
}
